// Live widget cards (busy-ai-srv#36, the Figma FINAL screens 13–19): the formatting every card
// shares — numbers, money, dates as the provider sent them. Pure, so the cards stay thin and the
// rules are tested without mounting anything.
//
// Dates and times arrive as WALL-CLOCK strings already in the right zone ("2026-10-26T20:00+00:00",
// "2026-10-31T06:20:00", "17:30"). They are read as text, never through `new Date(…)` in the
// browser's zone: a London forecast must say 20:00 to a user sitting in Istanbul too.

// The app's locale for the cards' digits (the frames are written in it: "4,195.30", "Mon 26 Oct").
export const LOCALE = 'en-GB'

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** A finite number out of a number or a numeric string; null otherwise. */
export const num = (v) => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v)
    return Number.isFinite(n) ? n : null
  }
  return null
}

/** "4,195.30" — grouped, between `min` and `max` decimals; '' for no number. */
export const fmtNumber = (v, { min = 0, max = 2 } = {}) => {
  const n = num(v)
  if (n === null) return ''
  return n.toLocaleString(LOCALE, { minimumFractionDigits: min, maximumFractionDigits: Math.max(min, max) })
}

/** Whole numbers stay whole ("£89"), anything else gets its pennies ("£12.50"). */
const decimalsFor = (n) => (Number.isInteger(n) ? 0 : 2)

/**
 * "£89", "$268.42", "4,182.40 TRY" when the code is not one Intl knows. `decimals` defaults to
 * whole-or-pennies (see decimalsFor).
 */
export const fmtMoney = (v, currency, { decimals } = {}) => {
  const n = num(v)
  if (n === null) return ''
  const d = decimals ?? decimalsFor(n)
  const code = String(currency || '').trim().toUpperCase()
  if (/^[A-Z]{3}$/.test(code)) {
    try {
      return n.toLocaleString(LOCALE, { style: 'currency', currency: code, currencyDisplay: 'narrowSymbol', minimumFractionDigits: d, maximumFractionDigits: d })
    } catch { /* an unknown code falls through to the plain form */ }
  }
  const plain = fmtNumber(n, { min: d, max: d })
  return code ? `${plain} ${code}` : plain
}

/** "+3.18" / "−1.05" (a real minus sign); '' for no number. */
export const fmtSigned = (v, decimals = 2) => {
  const n = num(v)
  if (n === null) return ''
  const body = fmtNumber(Math.abs(n), { min: decimals, max: decimals })
  return `${n < 0 ? '−' : '+'}${body}`
}

/** "+1.20%". */
export const fmtPct = (v, decimals = 2) => {
  const s = fmtSigned(v, decimals)
  return s ? `${s}%` : ''
}

/** Volume and market sizes: "52.31M", "4.01T", "980" — en-US, whose compact units are capitals. */
export const fmtCompact = (v) => {
  const n = num(v)
  if (n === null) return ''
  return n.toLocaleString('en-US', { notation: 'compact', maximumFractionDigits: 2 })
}

/** "13°" — a temperature rounded to the degree. */
export const fmtTemp = (v) => {
  const n = num(v)
  return n === null ? '' : `${Math.round(n)}°`
}

/**
 * The wall-clock parts of an ISO-ish string, read as TEXT (see the header): "2026-10-26",
 * "2026-10-26T20:00", "2026-10-26T20:00:00+03:00". Null when there is no date in it.
 */
export const isoParts = (s) => {
  const m = String(s ?? '').match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/)
  if (!m) return null
  return { y: +m[1], m: +m[2], d: +m[3], hh: m[4] ?? null, mm: m[5] ?? null }
}

// The weekday of a calendar date, independent of the browser's zone.
const weekday = (p) => DAYS[new Date(Date.UTC(p.y, p.m - 1, p.d)).getUTCDay()]

/** "Mon". */
export const dayShort = (s) => {
  const p = isoParts(s)
  return p ? weekday(p) : ''
}

/** "Mon 26 Oct". */
export const dateShort = (s) => {
  const p = isoParts(s)
  return p ? `${weekday(p)} ${p.d} ${MONTHS[p.m - 1]}` : ''
}

/** "26 Oct". */
export const dayMonth = (s) => {
  const p = isoParts(s)
  return p ? `${p.d} ${MONTHS[p.m - 1]}` : ''
}

/** "20:10" out of a datetime, or a bare "17:30"/"17:30:00"; '' when there is no time. */
export const timeOf = (s) => {
  const p = isoParts(s)
  if (p) return p.hh ? `${p.hh}:${p.mm}` : ''
  const m = String(s ?? '').match(/^(\d{1,2}):(\d{2})/)
  return m ? `${m[1].padStart(2, '0')}:${m[2]}` : ''
}

/** Whole days between two calendar dates (b − a); null when either is not a date. */
export const daysBetween = (a, b) => {
  const pa = isoParts(a)
  const pb = isoParts(b)
  if (!pa || !pb) return null
  return Math.round((Date.UTC(pb.y, pb.m - 1, pb.d) - Date.UTC(pa.y, pa.m - 1, pa.d)) / 86400000)
}

/** "3h 15m", "45m", "4h 00m" (the frame keeps the minutes when there are hours). */
export const durationLabel = (minutes) => {
  const n = num(minutes)
  if (n === null || n < 0) return ''
  const h = Math.floor(n / 60)
  const m = Math.round(n % 60)
  return h ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`
}

/** "Direct", "1 stop", "2 stops". */
export const stopsLabel = (stops) => {
  const n = num(stops)
  if (!n) return 'Direct'
  return `${n} stop${n === 1 ? '' : 's'}`
}

/** "1st", "2nd", "3rd", "4th", "11th", "22nd". */
export const ordinal = (v) => {
  const n = num(v)
  if (n === null) return ''
  const t = n % 100
  if (t >= 11 && t <= 13) return `${n}th`
  return `${n}${({ 1: 'st', 2: 'nd', 3: 'rd' })[n % 10] || 'th'}`
}

/** "1 adult" / "2 guests" — `noun` singular, an `s` added for more than one. */
export const countLabel = (v, noun) => {
  const n = num(v)
  if (n === null || n < 1) return ''
  return `${n} ${noun}${n === 1 ? '' : 's'}`
}

/**
 * A currency's flag as an emoji, from the ISO 4217 code's country part (USD → 🇺🇸, TRY → 🇹🇷,
 * EUR → 🇪🇺). '' for anything that is not a three-letter code.
 */
export const flagOf = (currency) => {
  const code = String(currency || '').trim().toUpperCase()
  if (!/^[A-Z]{3}$/.test(code)) return ''
  return [...code.slice(0, 2)].map(c => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65)).join('')
}

/** Only a web address may become a link (an `href` with `javascript:` in it is a hole). */
export const safeUrl = (u) => (typeof u === 'string' && /^https?:\/\//i.test(u.trim()) ? u.trim() : '')
